import cn from 'classnames';
import { forwardRef, useCallback, useMemo, useState } from 'react';

import { useFieldId } from '../../../utils';
import { BaseField, FieldLabel } from '../internal';

import { RadioGroupContext } from './radio-group-context';

import type { ForwardedRef, ReactElement } from 'react';
import type React from 'react';

import type { RadioGroupProps } from './radio-group.types';

import './radio-group.scss';

/**
 *
 * RadioGroup is a component that groups Radio components together,
 * ensuring that only one Radio within the group can be selected at a time.
 * It is used when there is a list of two or more options that are mutually
 * exclusive and the user must select exactly one choice
 *
 * ### Usage
 *
 * ```tsx
 * import { RadioGroup, RadioField } from '@bloomreach/react-banana-ui';
 *
 * export default function MyRadioFieldGroupComponent() {
 *  return (
 *  <RadioGroup label="Pick your favourite fruit">
 *      <RadioField label="Mango" value="mango" />
 *      <RadioField label="Banana" value="banana" />
 *      <RadioField label="Orange" value="orange" />
 *  </RadioGroup>
 * )};
 * ```
 *OR
 *
 * ### Usage
 *
 * ```tsx
 * import { RadioGroup, Radio } from '@bloomreach/react-banana-ui';
 *
 * export default function MyRadioGroupComponent() {
 *  return (
 *  <RadioGroup label="Radio Group" name="Radio group name">
 *      <Radio value="option1" />
 *      <Radio value="option2" />
 *      <Radio value="option3" />
 *  </RadioGroup>
 * )};
 * ```
 */

const RadioGroup = forwardRef(
  (props: RadioGroupProps, forwardedRef: ForwardedRef<HTMLInputElement> | null): ReactElement => {
    const {
      children,
      className,
      defaultValue,
      disabled = false,
      label,
      name: nameProp,
      onChange,
      orientation = 'vertical',
      readOnly = false,
      required = false,
      value,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const isControlled = value !== undefined;

    const [uncontrolledValue, setUncontrolledValue] = useState<null | string>(defaultValue ?? null);

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (!isControlled) {
          setUncontrolledValue(event.target.value);
        }
        onChange?.(event);
      },
      [isControlled, onChange, setUncontrolledValue],
    );

    // if name is not provided, generate a unique name
    const name = useFieldId(nameProp, 'rbui-radio-group-name');

    const contextProviderValue = useMemo(
      () => ({
        disabled,
        name,
        onChange: handleChange,
        readOnly,
        required,
        value: isControlled ? value : uncontrolledValue,
      }),
      [disabled, handleChange, isControlled, name, readOnly, required, uncontrolledValue, value],
    );

    const fieldLabel = label && <FieldLabel required={required}>{label}</FieldLabel>;

    return (
      <RadioGroupContext.Provider value={contextProviderValue}>
        <BaseField
          className={cn('rbui-radio-group', `rbui-radio-group--${orientation}`, className)}
          control={
            <div className={cn('rbui-radio-group__list')} role="radiogroup">
              {children}
            </div>
          }
          label={fieldLabel}
          labelPosition="block-start"
          ref={forwardedRef}
          required={required}
          {...rest}
        />
      </RadioGroupContext.Provider>
    );
  },
);

RadioGroup.displayName = 'RadioGroup';
export default RadioGroup;
